import { useCallback, useMemo, useState } from "react";
import * as React from "react";

import NewTouchpointForm from "components/new-touchpoint-form";

import { Patient } from "../services/types/patients.type";

type NewTouchpointProviderProps = {
  children?: React.ReactNode;
};

const NewTouchpointContext = React.createContext<any>(null);

export function NewTouchpointProvider({
  children
}: NewTouchpointProviderProps) {
  const [openModal, setOpenModal] = useState(false);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);
  const [initialDocuments, setInitialDocuments] = useState<File[]>([]);
  const onCloseCallbackRef = React.useRef<any>();

  const handleOpen = useCallback((patient?: Patient, callback?: () => void) => {
    setPatient(patient || null);
    setOpenModal(true);
    onCloseCallbackRef.current = callback;
  }, []);

  const handleClose = useCallback(() => {
    setOpenModal(false);
    setPatient(null);
    setInitialDocuments([]);
    if (onCloseCallbackRef.current) {
      onCloseCallbackRef.current();
    }
  }, []);

  const value = useMemo(
    () => ({
      handleOpen,
      selectedFiles,
      setSelectedFiles,
      setInitialDocuments
    }),
    [handleOpen, selectedFiles, setSelectedFiles, setInitialDocuments]
  );

  return (
    <NewTouchpointContext.Provider value={value}>
      {children}
      <NewTouchpointForm
        isOpen={openModal}
        onClose={handleClose}
        patient={patient}
        selectedFiles={selectedFiles}
        initialDocuments={initialDocuments}
      />
    </NewTouchpointContext.Provider>
  );
}

export default function useNewTouchpoint() {
  const context = React.useContext(NewTouchpointContext);
  if (!context) {
    throw new Error(
      `useNewTouchpoint must be used within a NewTouchpointProvider`
    );
  }
  const { handleOpen, selectedFiles, setSelectedFiles, setInitialDocuments } =
    context;
  return {
    handleOpen,
    selectedFiles,
    setSelectedFiles,
    setInitialDocuments
  };
}
